import {identity, isUndefined, uniqBy} from 'lodash';
import {compose} from 'redux';
import uuid from 'uuid-random';

import {Mutable} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  createObjectPropertyUpdater,
  createResourceTableValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {logger} from '../../../../srcCommon/services/logger/logger';
import {OfflineError} from '../../../../srcCommon/services/offline/offlineError';
import {hasPersistedStorageAsync} from '../../../helpers/storageHelpers';
import {AnnotationModel, serializeAnnotation} from '../../../models/api/annotationModel';
import {
  isAttachmentModel,
  serializeAttachment,
  UploadedAttachmentModel,
} from '../../../models/api/attachmentModel';
import {CommentErrorsEnum, CommentModel, CommentStatusesEnum} from '../../../models/api/commentModel';
import {ApiCommentDataGroup, DataGroupModel} from '../../../models/api/dataGroupModel';
import {putApiAsync} from '../../../services/http/http';
import {HttpError, HttpNetworkError, HttpTimeoutError} from '../../../services/http/httpError';
import {findCommentLock} from '../../../services/locks/resourceLocks';
import {ThunkAsyncAction} from '../../appThunk';
import {
  finishOptimisticUpdateAction,
  startOptimisticUpdateAction,
} from '../../optimisticUpdates/optimisticUpdatesActions';
import {OptimisticUpdater} from '../../optimisticUpdates/optimisticUpdatesState';
import {ViewerPanelId} from '../../panels/panelStateBase';
import {updateDataAction} from '../dataGroupsActions';
import {getOriginalData} from '../dataSelectors';
import {DataState} from '../dataState';
import {addCommentToCacheAsync, removeCommentFromCacheAsync} from './commentsActionsHelpers';

const offlineContentLogger = logger.getLogger('offlineContent');

/*
 * Types.
 */

export interface PublicSaveCommentOptions {
  newText?: string;
  addedAttachments?: ReadonlyArray<UploadedAttachmentModel>;
  removedAttachmentCids?: ReadonlyArray<string>;
  newTeammateIds?: ReadonlyArray<number>;
  newTopic?: string;
  newReferencedActivityId?: number | null;
  newAnnotation?: AnnotationModel | null;
}

interface SaveCommentOptions extends PublicSaveCommentOptions {
  panelId?: ViewerPanelId;
  /** If set, the comment will only be saved if there are unsaved changes. */
  onlyIfDirty?: boolean;
}

/*
 * Action creators.
 */

export function saveCommentAsyncAction(
  composerComment: CommentModel,
  options: SaveCommentOptions,
): ThunkAsyncAction<CommentModel | undefined> {
  const commentUid = composerComment.uid;

  const optimisticUpdateId = uuid();
  const optimisticUpdater = createOptimisticUpdater(composerComment, options);

  return async (dispatch, getState) => {
    // Wrap in a try/catch just in case to guard against a hanging optimistic update.
    dispatch(startOptimisticUpdateAction(optimisticUpdateId, optimisticUpdater));
    try {
      // Lock the comment so our update is transactionally safe.
      return await findCommentLock(commentUid).lockAsync(async () => {
        // Find the original version of the existing comment, or use the composer version if it is a new comment.
        const state = getState();
        const originalData = getOriginalData(state);
        const {byId, deletedIds} = originalData.comments;

        if (deletedIds.contains(commentUid)) {
          dispatch(finishOptimisticUpdateAction(optimisticUpdateId));
          return undefined;
        }

        const existingComment = byId[commentUid];
        const comment = existingComment || composerComment;

        if (options.onlyIfDirty && comment.conversationId && !comment.isSaving) {
          dispatch(finishOptimisticUpdateAction(optimisticUpdateId));
          return comment;
        }

        // Try to save it.
        const newComment = updateComment(comment, options);
        const resultDataGroup = await trySaveCommentOnlineOrOfflineAsync(newComment);

        // Update the store and finish the optimistic update at the same time.
        dispatch(finishOptimisticUpdateAction(optimisticUpdateId, updateDataAction(resultDataGroup)));

        return findCommentInDataGroup(resultDataGroup, commentUid);
      });
    } catch (error) {
      dispatch(finishOptimisticUpdateAction(optimisticUpdateId));
      throw error;
    }
  };
}

export function saveCommentOnlineAsyncAction(
  comment: CommentModel,
): ThunkAsyncAction<CommentModel | undefined> {
  return async (dispatch) => {
    const resultDataGroup = await saveCommentOnlineAsync(comment);
    dispatch(updateDataAction(resultDataGroup));
    return findCommentInDataGroup(resultDataGroup, comment.uid);
  };
}

export function flushCommentAsyncAction(commentUid: string): ThunkAsyncAction {
  return async (dispatch, getState) => {
    offlineContentLogger.debug('Flushing offline comment', {
      comment_uid: commentUid,
    });

    const state = getState();
    const originalData = getOriginalData(state);
    const comment = originalData.comments.byId[commentUid];

    if (!comment || comment.status !== CommentStatusesEnum.DRAFT || !comment.isSaving) {
      return;
    }

    try {
      // Try to save online.
      await dispatch(saveCommentOnlineAsyncAction(comment));

      // Clean up client-side storage.
      tryRemoveCommentOffline(comment);
    } catch (error) {
      // If we're offline, try again later.
      const isOfflineError = error instanceof HttpNetworkError || error instanceof HttpTimeoutError;
      if (isOfflineError) {
        throw new OfflineError(error);
      }

      // Report non-HTTP errors to Bugsnag.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      // If this comment failed to save within 15 days, remove it from offline storage and stop trying.
      const age = Date.now() - Math.max(comment.createdAt, comment.updatedAt);
      if (age > maxSaveOfflineAge) {
        tryRemoveCommentOffline(comment);
      }

      // Put the draft in a failed state.
      const failedComment = {
        ...comment,
        error: CommentErrorsEnum.CANT_SAVE,
      };

      // Update the store.
      dispatch(updateDataAction({comments: [failedComment]}));
    }
  };
}

/*
 * Helpers.
 */

function updateComment(comment: CommentModel, options: SaveCommentOptions): CommentModel {
  const updatedComment: Mutable<CommentModel> = {
    ...comment,
    lastPanelId: options.panelId || comment.lastPanelId,
    isSaving: true,
  };

  const {addedAttachments, removedAttachmentCids, newReferencedActivityId, newAnnotation} = options;

  if (!isUndefined(options.newText)) {
    updatedComment.text = options.newText;
  }

  if (!isUndefined(addedAttachments)) {
    updatedComment.attachments = uniqBy(
      [...updatedComment.attachments, ...addedAttachments],
      (a) => isAttachmentModel(a) && a.cid,
    );
  }

  if (!isUndefined(removedAttachmentCids)) {
    updatedComment.attachments = updatedComment.attachments.filter(
      (a) => isAttachmentModel(a) && !removedAttachmentCids.includes(a.cid),
    );
  }

  if (!isUndefined(options.newTeammateIds)) {
    updatedComment.teammateIds = options.newTeammateIds;
  }

  if (!isUndefined(options.newTopic)) {
    updatedComment.topic = options.newTopic;
  }

  if (!isUndefined(newReferencedActivityId)) {
    updatedComment.referencedActivityId = newReferencedActivityId || undefined;
  }

  if (!isUndefined(newAnnotation)) {
    updatedComment.annotation = newAnnotation || undefined;
  }

  return updatedComment;
}

function createOptimisticUpdater(
  composerComment: CommentModel,
  options: SaveCommentOptions,
): OptimisticUpdater {
  const commentUid = composerComment.uid;
  const commentUpdater = createUpdater<CommentModel>((comment) => updateComment(comment, options));

  const optimisticUpdaterForComment = createObjectPropertyUpdater<DataState, 'comments'>(
    'comments',
    createUpdater((comments) => {
      const {byId, deletedIds} = comments;

      if (deletedIds.contains(commentUid)) {
        return comments;
      }

      const existingComment = byId[commentUid];
      const comment = existingComment || composerComment;

      return {
        ...comments,
        byId: {
          ...byId,
          [commentUid]: commentUpdater(comment),
        },
      };
    }),
  );

  const optimisticUpdaterForConversation =
    composerComment.status === CommentStatusesEnum.DRAFT
      ? createObjectPropertyUpdater<DataState, 'conversations'>(
          'conversations',
          createResourceTableValueUpdater(composerComment.conversationId, (conversation) => {
            if (conversation.draftCommentUids.includes(commentUid)) {
              return conversation;
            }

            return {
              ...conversation,
              draftCommentUids: [...conversation.draftCommentUids, commentUid],
            };
          }),
        )
      : identity;

  return compose(optimisticUpdaterForComment, optimisticUpdaterForConversation);
}

async function trySaveCommentOnlineOrOfflineAsync(comment: CommentModel) {
  try {
    return await saveCommentOnlineOrOfflineAsync(comment);
  } catch (error) {
    // Report non-HTTP errors to Bugsnag.
    if (!(error instanceof HttpError)) {
      reportToBugsnag(error);
    }

    // Put the draft in a failed state.
    const failedComment = {
      ...comment,
      error: CommentErrorsEnum.CANT_SAVE,
    };

    return {comments: [failedComment]};
  }
}

const maxSaveOfflineAge = 15 * 24 * 60 * 60 * 1000;

async function saveCommentOnlineOrOfflineAsync(comment: CommentModel) {
  try {
    // Try to save online.
    const result = await saveCommentOnlineAsync(comment);

    // Clean up client-side storage.
    tryRemoveCommentOffline(comment);

    return result;
  } catch (error) {
    // Can we save offline instead?
    const isOfflineError = error instanceof HttpNetworkError || error instanceof HttpTimeoutError;
    if (!isOfflineError) {
      throw error;
    }

    // Try to save offline.
    return await saveCommentOfflineAsync(comment, error);
  }
}

export async function saveCommentOnlineAsync(comment: CommentModel): Promise<DataGroupModel> {
  const body = {
    text: comment.text,
    attachments: comment.attachments.map((a) => isAttachmentModel(a) && serializeAttachment(a)),
    referenced_activity_id: comment.referencedActivityId || null,
    annotation: comment.annotation ? serializeAnnotation(comment.annotation) : null,
  };

  const serverDataGroup = await putApiAsync(
    ApiCommentDataGroup,
    `${comment.url}?include_conversation=true`,
    body,
  );

  // Merge the client-side changes into the result to make them permanent (panel ID, recipients, topic, etc.)
  return {
    ...serverDataGroup,
    comments: (serverDataGroup.comments || []).map((serverComment) => {
      if (serverComment.uid !== comment.uid) {
        return serverComment;
      }

      return {
        ...comment,
        // Don't make any errors permanent, though!
        error: undefined,
        ...serverComment,
      };
    }),
  };
}

export async function saveCommentOfflineAsync(
  comment: CommentModel,
  throwIfUnsupported: unknown,
): Promise<DataGroupModel> {
  // Save the comment to client-side storage.
  const [hasPersistedStorage] = await Promise.all([
    hasPersistedStorageAsync(),
    addCommentToCacheAsync(comment),
  ]);

  // Our comment is guaranteed to be safe only if we have persisted storage, so re-throw the original error.
  if (!hasPersistedStorage) {
    throw throwIfUnsupported;
  }

  offlineContentLogger.debug('Saved offline comment', {
    comment_uid: comment.uid,
  });

  return {comments: [comment]};
}

export function tryRemoveCommentOffline(comment: CommentModel) {
  removeCommentFromCacheAsync(comment).catch((error) => {
    reportToBugsnag(error);
  });
}

export function findCommentInDataGroup(
  dataGroup: DataGroupModel,
  commentUid: string,
): CommentModel | undefined {
  return dataGroup.comments?.find((comment) => comment.uid === commentUid);
}
